import { FC, MouseEvent, useMemo, useState, useContext } from "react";

import { TrainingDrillFolder } from "@/features/training/model/Training/TrainingDrillFolder";
import { EditDrillFolder } from "@/features/training/pages/drill-tab/panel-left/accordion/EditDrillFolder";
import { TreeEventItem } from "@/features/training/pages/drill-tab/panel-left/accordion/TreeEventItem";
import { TreeExerciseItem } from "@/features/training/pages/drill-tab/panel-left/accordion/TreeExerciseItem";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


interface TreeDrillFolderItemProps {
	folder: TrainingDrillFolder;
	i: number;
}

export const TreeDrillFolderItem: FC<TreeDrillFolderItemProps> = ({
	folder,
	i,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const [open, setOpen] = useState(false);

	const drills = useMemo(
		() =>
			folder.Drills?.map((x) => (
				<TreeExerciseItem key={x.Id} id={x.Id} name={x.Name} />
			)) ?? [],
		[folder.Drills],
	);

	const events = useMemo(
		() =>
			folder.Events?.map((x) => (
				<TreeEventItem key={x.Id} id={x.Id} name={x.Name} />
			)) ?? [],
		[folder.Events],
	);

	const drillCount = folder.Drills?.length ?? 0;
	const eventCount = folder.Events?.length ?? 0;

	const evenBg = alpha(theme.palette.action.hover, 0.1);

	return (
		<>
			<TreeItem
				itemId={folder.Id ? `drill-folder-${folder.Id}` : folder.Name}
				label={
					<Box
						id={
							folder.Id != null
								? `tree-node-drill-folder-${folder.Id}`
								: undefined
						}
						aria-label={`Drill folder ${folder.Name}`}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							py: 0.75,
							pr: 1,
							pl: 0.5,
							minWidth: 0,
						}}
					>
						<FolderOutlinedIcon
							fontSize="small"
							sx={{ color: "action.active", mx: 1 }}
						/>

						<Typography
							variant="body2"
							fontWeight={600}
							sx={{
								minWidth: 0,
								flex: 1,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
							title={folder.Name}
						>
							{folder.Name}
						</Typography>

						{/* Count chips */}
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.5,
								mr: 0.5,
								flexShrink: 0,
							}}
						>
							{drillCount > 0 && (
								<Chip
									sx={{
										height: 22,
										borderRadius: 1.25,
										"& .MuiChip-label": {
											px: 1,
											fontWeight: 700,
										},
									}}
									size="small"
									variant="outlined"
									icon={<FitnessCenterOutlinedIcon sx={{ fontSize: 16 }} />}
									label={drillCount}
								/>
							)}
							{eventCount > 0 && (
								<Chip
									sx={{
										height: 22,
										borderRadius: 1.25,
										"& .MuiChip-label": {
											px: 1,
											fontWeight: 700,
										},
									}}
									size="small"
									variant="outlined"
									icon={<EventNoteOutlinedIcon sx={{ fontSize: 16 }} />}
									label={eventCount}
								/>
							)}
						</Box>

						{/* Edit (reveals on hover) */}
						<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.TreeDrillFolderItem.L142C22.EditFolder", "Edit folder")} disableInteractive>
							<span>
								<IconButton
									className="folder-edit"
									size="small"
									disabled={folder.Id == null}
									onClick={(e: MouseEvent<HTMLButtonElement>) => {
										e.stopPropagation();
										setOpen(true);
									}}
									sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
								>
									<EditOutlinedIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Box>
				}
				sx={{
					"& > .MuiTreeItem-content": {
						borderRadius: 1.5,
						backgroundColor: i % 2 === 0 ? evenBg : "transparent",
						transition: "background-color 120ms ease",
						"&:hover": {
							backgroundColor:
								i % 2 === 0
									? alpha(theme.palette.action.hover, 0.15)
									: theme.palette.action.hover,
						},
						"&:hover .folder-edit": { opacity: 1 },
					},
					"& .MuiTreeItem-label": { width: "100%" },
				}}
			>
				{drills}
				{events}
			</TreeItem>

			{/* Dialog rendered outside of tree label for proper focus & events */}
			<EditDrillFolder
				id={folder.Id}
				open={open}
				handleClose={() => setOpen(false)}
			/>
		</>
	);
};
